import { Box, Typography } from '@mui/material'
import React from 'react'

import type { EvidenceStepView } from '../../../domain/activeInspection'
import { getTranslate, Locale } from '../../../i18n'
import { convertToGalleryEvidence } from '../../../utils/evidenceUtils'
import { InspectionStepGallery } from '../../molecules/InspectionStepGallery'

interface TimelineProps {
  /**
   * The steps the page's header strip renders, in its order and with its
   * statuses. Taking them rather than re-deriving them from the summary view is
   * what keeps each gallery in step with its circle: same steps, same colors.
   */
  steps: EvidenceStepView[]
  locale: Locale
  onImageClick: (imagePath: string) => void
  /** An action for a step's header, when the page offers one. */
  renderStepAction?: (step: EvidenceStepView) => React.ReactNode
}

/**
 * One always-visible gallery per step of the inspection's flow, laid out for
 * the backoffice's laptop form factor.
 */
export const Timeline: React.FC<TimelineProps> = ({ steps, locale, onImageClick, renderStepAction }) => {
  const t = getTranslate(locale)

  if (steps.length === 0) {
    return (
      <Box sx={{ mt: 2, p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          {t('resources.historical_inspections.timeline.no_steps')}
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ mt: 2 }}>
      {steps.map(step => (
        <InspectionStepGallery
          key={step.stepId}
          stepId={step.stepId}
          stepStatus={step.status}
          stepTitle={t(`resources.historical_inspections.enums.inspection_step.${step.stepId}`)}
          evidence={convertToGalleryEvidence(step.evidence)}
          onImageClick={onImageClick}
          action={renderStepAction?.(step)}
        />
      ))}
    </Box>
  )
}
